import React, { useCallback, useEffect } from 'react';
import { Avatar } from '../ui/Avatar';
import { PillButton } from '../ui/PillButton';
import { IButton } from '../../domain/message.interface';
import { RichMessageType } from '../../domain/richMessage.interface';
import { TextCard } from './cards/TextCard';
import { WayfindingCard } from './cards/WayfindingCard';
import { RegistrationCard } from './cards/RegistrationCard';
import { SessionsCard } from './cards/SessionsCard';
import { ArticlesCard } from './cards/ArticlesCard';
import { ExecutivesCard } from './cards/ExecutivesCard';
import { PrizeCard } from './cards/PrizeCard';
import { QuestionSingleResponseCard } from './cards/QuestionSingleResponseCard';
import { useChatContext } from '../../context/chat/chat.context';

interface AssistantMessageProps {
  message: string;
  buttons: IButton[];
  selectButtons: (labels: string[]) => void;
  setEnabledInput: (enabled: boolean) => void;
}

// Strip raw control characters (U+0000–U+001F except \t \n \r) that the backend
// occasionally embeds in string values (e.g. \b as a visual separator), which are
// illegal inside JSON strings and would cause JSON.parse to throw.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F]/g;
const sanitizeForJsonParse = (content: string): string =>
  content.replace(CONTROL_CHARS_RE, '');

const parseRichMessage = (content: string): RichMessageType | null => {
  try {
    const parsed = JSON.parse(sanitizeForJsonParse(content));
    if (parsed && typeof parsed.type === 'string') return parsed as RichMessageType;
    return null;
  } catch {
    return null;
  }
};

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  message,
  buttons,
  selectButtons,
  setEnabledInput,
}) => {
  const { handleChatSubmit } = useChatContext();
  const rich = parseRichMessage(message);
  const hasButtons = buttons.length > 0;

  useEffect(() => {
    setEnabledInput(!hasButtons);
  }, [hasButtons, setEnabledInput]);

  const handlePillClick = useCallback((label: string) => {
    selectButtons([label]);
  }, [selectButtons]);

  const renderCard = () => {
    if (!rich) return <TextCard message={message} />;

    switch (rich.type) {
      case 'wayfinding':
        return <WayfindingCard data={rich} />;
      case 'registration_summary':
        return <RegistrationCard data={rich} />;
      case 'session_recommendations':
        return <SessionsCard data={rich} />;
      case 'text_with_articles':
        return <ArticlesCard data={rich} />;
      case 'executives':
        return <ExecutivesCard data={rich} />;
      case 'prize_cards':
        return <PrizeCard data={rich} onSelect={(labels) => handleChatSubmit(labels[0])} />;
      case 'question_single_response':
        return <QuestionSingleResponseCard data={rich} onSelect={(labels) => handleChatSubmit(labels[0])} />;
      default:
        return <TextCard message={message} />;
    }
  };

  return (
    <div className="flex items-start gap-2 px-4">
      <Avatar />
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        {renderCard()}
        {hasButtons && rich?.type !== 'prize_cards' && (
          <div className="flex flex-wrap gap-2">
            {buttons.flatMap((btn) =>
              btn.options.map((opt) => (
                <PillButton
                  key={opt.id}
                  label={opt.label}
                  onClick={() => handlePillClick(opt.label)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
